import "./index.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
} from "react-router-dom";
import { useEffect } from "react";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { useAuth } from "react-oidc-context";

import DocumentList from "./components/documents/DocumentList";
import DocumentEditor from "./components/editor/DocumentEditor";
import JournalEditor from "./components/journal/JournalEditor";
import Header from "./components/layout/Header";
import HelpPage from "./components/help/HelpPage";
import OverviewPage from "./components/help/OverviewPage";

const theme = createTheme();

function Home() {
  const auth = useAuth();

  useEffect(() => {
    document.title = "JokeLab";
  }, []);

  const signOutRedirect = () => {
    auth.removeUser(); // clears local tokens
    const clientId = "76u29f2ll32p5p6jm4ovf85oro";
    const logoutUri = process.env.REACT_APP_REDIRECT_URI;
    const cognitoDomain =
      "https://us-east-1bo5zyrska.auth.us-east-1.amazoncognito.com";
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  if (auth.isLoading) {
    return <div>Loading...</div>;
  }

  // Background silent-renew attempts (see index.js) fail harmlessly when a
  // refresh token has expired or is invalid - treat that like "signed out"
  // rather than wedging the app on a fatal error screen with no way back
  // to the sign-in button.
  if (auth.error && auth.error.source !== "signinSilent") {
    return <div>Encountering error... {auth.error.message}</div>;
  }

  if (auth.isAuthenticated) {
    return (
      <div
        className="min-h-screen w-full flex flex-col items-center mt-0 pt-0"
        style={{ backgroundColor: "#e6dcc6" }} // slightly darker margin color
      >
        <div
          className="w-full max-w-5xl flex flex-col items-center"
          style={{
            backgroundColor: "#f7f2e3",
            minHeight: "100vh", // ensures center div is at least as high as the browser
            padding: 0, // remove any default padding
          }}
        >
          <Header
            user={auth.user}
            onLogout={signOutRedirect}
            className="w-full"
            style={{
              marginTop: 0,
              paddingTop: 0,
              paddingBottom: 0,
              width: "100%", // Make header full width of parent
            }}
          />
          <div className="w-full max-w-5xl">
            <DocumentList token={auth.user?.access_token} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ backgroundColor: "#f7f2e3" }} // less bright background
    >
      {/* Logo */}
      <div className="flex flex-col items-center mb-8">
        <div className="flex items-center gap-2">
          <img src="/jokelab_welcome.jpg" alt="JokeLab Logo" width="300px" /> {/* 50% smaller */}
        </div>
      </div>
      {/* Explanation block moved above sign in */}
      <div
        style={{
          maxWidth: "400px",
          margin: "0 auto 2rem auto",
          color: "#1e293b",
          fontSize: "1.08rem",
          background: "none",
          boxShadow: "none",
          borderRadius: "0",
          padding: "0",
        }}
      >
        <p>
          JokeLab helps write and organize standup comedy. For a quick start, check out the <a href="https://youtu.be/_3ZpDTDX_1s" target="_blank" rel="noopener" style={{ color: "#2563eb", fontWeight: "bold" }}>video</a> or the <Link to="/help" style={{ color: "#2563eb", fontWeight: "bold" }}>help page</Link>. Questions? Join our <a href="https://discord.gg/xjMRmBVAQv" target="_blank" rel="noopener" style={{ color: "#2563eb", fontWeight: "bold" }}>discord</a>.
        </p>
      </div>
      <div className="flex gap-4">
        <button
          onClick={() => auth.signinRedirect()}
          className="bg-yellow-400 text-green-900 font-semibold py-2 px-6 rounded-lg hover:bg-yellow-300 transition"
        >
          Sign in
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/document_editor" element={<DocumentEditor />} />
          <Route path="/document_editor/:id" element={<DocumentEditor />} />
          <Route path="/journal_editor" element={<JournalEditor />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/overview" element={<OverviewPage />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
